<template>
  <div class="MyAccountPage_Support">
    <transition name="fade" mode="out-in" v-if="isLoading">
      <app-loading
        :is-loading="isLoading"
        :spinner-color="'#1474a4'"
      />
    </transition>
    <template v-else>
      <h2 class="title">
        {{ $t('myaccount.support.title', 'Need help?') }}
      </h2>

      <div class="ContactWrapper">
        <p class="tip">
          {{ $t('myaccount.support.descriptions', 'Any questions on your Lumea Prestige subscription? We are here to help you.') }}
        </p>

        <form class="Contact_form" v-if="contactFormState === 'on'">
          <h3 class="title title-form">
            {{ $t('myaccount.support.form.title', 'Contact form') }}
          </h3>
          <div
            class="SelectGroup Form_input-withBorder"
            :class="{'error': errorsFor('selectedContactTitle').length > 0}"
          >
            <label class="Form_label">{{ $t('myaccount.support.form.textLable.title', 'Title') }}</label>
            <app-select
              :placeholder="$t('myaccount.support.form.placeholder.title')"
              v-model="selectedContactTitle"
              :options="contactTitles"
              :searchable="false"
              :allow-empty="false"
              :show-labels="false"
              open-direction="bottom"
              data-name="contact-selectTitle"
              :disabled="submitContactForm.isActive"
            />
            <p class="tip-error" v-for="(error, index) in errorsFor('selectedContactTitle')" :key="index">
              {{ $t('myaccount.support.form.textLable.title', 'Title') }} {{ error }}
            </p>
          </div>
          <div class="Form_input-withBorder">
            <app-text-input
              :errors="errorsFor('userEmail')"
              class="Form_textarea-email"
              :label="$t('myaccount.support.form.textLable.email', 'Email:')"
              v-model.trim="userEmail"
              data-name="myaccountSupport-email"
              :disabled="submitContactForm.isActive"
            />
          </div>
          <app-textarea
            :label="$t('myaccount.support.form.textLable.content', 'How can we help?')"
            class="Form_textarea-withBackground"
            :errors="errorsFor('userQuestion')"
            v-model.trim="userQuestion"
            data-name="myAccountSupport-helpText"
            :disabled="submitContactForm.isActive"
          />

          <app-button
            type="submit"
            @click.prevent="submitContactForm.run()"
            :is-loading="submitContactForm.isActive"
            data-name="myAccountSupport-submitButton"
          >
            {{ $t('myaccount.support.form.button', 'Submit your question') }} ›
          </app-button>
        </form>

        <p class="Contact_phone">
          <span class="phoneBox">
            <app-icon name="phone" />
          </span>
          <i class="tips">
            {{ $t('myaccount.support.phone.textLable', 'Or call us at') }}:
          </i>
          <span class="phoneNumber">
            {{ $t('myaccount.support.phoneNumber', '0800 1234567') }}
          </span>
        </p>

        <my-account-faq :product-faq="productFaq"/>
      </div>
    </template>
  </div>
</template>
<script>
  import { required, email, maxLength, minLength } from 'vuelidate/lib/validators'
  import { getErrors, extractErrorMessage } from '@/utilities/common'
  import MyAccountFaq from 'components/MyAccountFaq'
  import Vue from 'vue'
  import { mapState } from 'vuex'

  export default {
    name: 'support',

    data () {
      return {
        showValidations: false,
        isLoading: true,
        productFaq: [],
        userEmail: '',
        userQuestion: '',
        selectedContactTitle: null
      }
    },

    computed: {
      ...mapState('culture', ['contactFormState', 'contactTitles'])
    },

    validations: {
      userEmail: {
        required,
        email,
        minLength: minLength(5),
        maxLength: maxLength(40)
      },
      userQuestion: {
        required,
        maxLength: maxLength(200),
        minLength: minLength(10)
      },
      selectedContactTitle: {
        required
      }
    },

    methods: {
      errorsFor (field) {
        if (!this.showValidations) {
          return []
        }

        if (this.$v[field] == null) {
          return []
        }

        return getErrors(this.$v[field])
      }
    },

    tasks (t) {
      return {
        submitContactForm: t(async function () {
          this.$v.$touch()
          if (this.$v.$invalid) {
            this.showValidations = true
          } else {
            this.trackConversion({ name: 'info_request' })
            await Vue.api.support.submitQuestion(this.userEmail, this.selectedContactTitle, this.userQuestion).then(() => {
              this.$notify({
                type: 'success',
                title: this.$t('myaccount.support.submitQuestion.success', 'The question has been sent to administrators')
              })
            })
          }
        }).onError(function (task) {
          this.$notify({
            type: 'error',
            title: extractErrorMessage(task.error)
          })
        }).flow('drop')
      }
    },

    created () {
      Vue.api.product.getFaq().then((prodcutFaq) => {
        this.productFaq = prodcutFaq
        this.isLoading = false
      }).catch(error => {
        this.$notify({
          type: 'error',
          title: extractErrorMessage(error)
        })
      })
    },

    components: {
      MyAccountFaq
    }
  }
</script>

<style lang="scss">
  .MyAccountPage {
    &_Support {
      .title {
        padding: 0 0 .08rem;

        @include media(tablet) {
          padding: 0 .15rem .12rem;
        }
      }

      .tip {
        font-size: .17rem;
        color: $font-black;
        font-weight: 500;
        line-height: .25rem;
      }

      .ContactWrapper {
        @include media(tablet) {
          padding: .18rem .15rem;
        }
      }

      .Contact_form {
        padding-top: .35rem;

        .title-form {
          margin-bottom: .17rem;
          border: none;
          padding: 0;
        }

        .multiselect__tags {
          border: solid .01rem #9b9b9b;
          background-color: rgba(216, 216, 216, 0.25);
          padding: .08rem .1rem .07rem;
          line-height: .23rem;
          height: .4rem;

          .multiselect__single {
            background: none;
          }
        }

        .multiselect__content-wrapper {
          border: .01rem solid #9b9b9b;
        }

        .SelectGroup,
        .Form_group {
          margin-bottom: .17rem;
        }

        .Form_textarea {
          &.Form_group {
            margin-bottom: .2rem;

            @include media(tablet) {
              margin-bottom: .23rem;
            }
          }
        }
      }

      .AppButton {
        min-width: 2.63rem;
      }

      .Contact_phone {
        margin-top: .2rem;
        font-size: .18rem;
        color: #4a4a4a;
        font-weight: 500;
      }

      .phoneBox {
        display: inline-flex;
        justify-content: center;
        vertical-align: middle;
        align-items: center;
        width: .5rem;
        height: .5rem;
        background-color: rgba(0, 102, 116, .1);
        border-radius: .02rem;
        margin-right: .1rem;

        .icon-phone {
          color: $blue;
        }
      }

      .phoneNumber {
        color: #0066a1;
      }
    }
  }
</style>
